/**
 * Destek mesajına yanıt taslağı (17.10.2026).
 *
 * **Bu dosya ekran metni değil, müşteriye gidecek mektup.** Ekranın kendi metinleri `metin/`
 * altında ve yalnız Türkçe; ama mektup müşterinin yazdığı dilde gitmeli (Türkçe ya da İngilizce),
 * ve `scripts/metin-tamlik.test.mjs` metin tablosunda İngilizce kalmış girişi hata sayıyor.
 * İki ayrı şey olduğu için mektup şablonu burada.
 *
 * CRM e-posta **göndermez**: taslak `mailto:` ile personelin e-posta uygulamasında açılır ya da
 * kopyalanır. Dış mesaj kararı Ürün Sahibi'nde (17.10.2026 işi, bkz. rapor).
 */

export type YanitDili = 'tr' | 'en';

export interface YanitGirdisi {
  email: string;
  subject: string;
  reference: string;
  name: string;
  dil: YanitDili;
}

export interface YanitTaslagi {
  /** Okunabilir ve güvenli bir adres; değilse `null` ve bağlantı kurulmaz. */
  to: string | null;
  subject: string;
  body: string;
  mailto: string | null;
}

/** Başlık enjeksiyonuna ve `mailto:` parametre kaçırmaya (`?cc=`, `&bcc=`) izin vermeyen sade adres. */
const GUVENLI_ADRES = /^[A-Za-z0-9.!#$*+/=^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9.-]*[A-Za-z0-9])?\.[A-Za-z]{2,}$/;

function tekSatir(metin: string): string {
  // eslint-disable-next-line no-control-regex -- denetim karakterleri başlığa girmesin
  return metin.replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/** "Ayşe Yılmaz" -> "Ayşe". Ad yoksa ya da yer tutucuysa `null`. */
function ilkAd(ad: string): string | null {
  const kelime = tekSatir(ad).split(' ')[0] ?? '';
  if (kelime === '' || /^(ziyaretçi|ziyaretci|visitor|misafir|guest)$/i.test(kelime)) return null;
  return kelime.slice(0, 40);
}

/**
 * Kodlanmış `mailto:` bağlantısının güvenli uzunluk eşiği. Bazı e-posta uygulamaları ve tarayıcılar
 * uzun bağlantıyı keser ya da hiç açmaz; eşik aşılınca ekran "metni kopyalayıp yapıştır" der.
 */
export const MAILTO_ESIK = 1800;

export interface MailtoSonucu {
  /** `null`: adres güvenli değil, bağlantı kurulmaz. */
  href: string | null;
  /** Kodlanmış bağlantı eşiği aştı: e-posta uygulaması metni kesebilir. */
  uzun: boolean;
}

/**
 * Düzenlenmiş konu ve gövdeden `mailto:` kurar. Adres yalnız `GUVENLI_ADRES` kalıbından geçerse girer
 * ve bağlantıya olduğu gibi yazılır; konu ve gövde yalnız `encodeURIComponent` ile kodlanır. Kullanıcı
 * metnindeki `&bcc=`, `?cc=`, satır sonu + `Bcc:` kodlandığı için ayrı parametre ya da başlık olamaz.
 * Konu ek olarak tek satıra indirilir (denetim karakterleri başlığa girmez).
 */
export function mailtoKur(to: string, subject: string, body: string): MailtoSonucu {
  const adres = to.trim();
  if (!GUVENLI_ADRES.test(adres)) return { href: null, uzun: false };
  const href = `mailto:${adres}?subject=${encodeURIComponent(tekSatir(subject))}&body=${encodeURIComponent(body)}`;
  return { href, uzun: href.length > MAILTO_ESIK };
}

export function yanitTaslagi(g: YanitGirdisi): YanitTaslagi {
  const adres = g.email.trim();
  const to = GUVENLI_ADRES.test(adres) ? adres : null;
  const konu = tekSatir(g.subject);
  const ref = tekSatir(g.reference);
  const ad = ilkAd(g.name);
  const onEk = /^re\s*:/i.test(konu) ? '' : 'Re: ';
  const subject = `${onEk}${konu} (${ref})`;
  const body =
    g.dil === 'tr'
      ? `${ad ? `Merhaba ${ad},` : 'Merhaba,'}\n\n${ref} numaralı talebiniz için teşekkür ederiz.\n\n\n\nSaygılarımızla,\nMBD Travel`
      : `${ad ? `Hello ${ad},` : 'Hello,'}\n\nThank you for contacting us about request ${ref}.\n\n\n\nKind regards,\nMBD Travel`;
  const mailto = to === null ? null : mailtoKur(to, subject, body).href;
  return { to, subject, body, mailto };
}
